// CreateUnitDrawer — the "New unit" form: name, parent picker, computed tier,
// description, location group (with the geofence radius), tags
// → POST /company-structure/add-node.

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Button, Card, Chip, Drawer, Dropdown, Field, Icon } from '@/components/common';
import { useAddNodeMutation, useGetCompanyStructureQuery } from '@/api/endpoints/structureApi';
import { useLevelConfig } from '@/api/endpoints/levelsApi';
import type { OrgUnit } from '@/mocks/structure';
import { UnitFields, emptyDraft, type UnitDraft } from './unitForm';
import { hasErrors, validateUnitDraft } from './utils/unitValidation';

interface CreateUnitDrawerProps {
  open: boolean;
  /** Default parent id (the Add-unit trigger passes the company root or a unit). */
  parentId?: string;
  onClose: () => void;
  /** Called after a successful create with the new unit's parent id. */
  onCreated: (parentId: string) => void;
}

/** Depth-first flatten of the tree limited to units that can take children (level 0–3). */
const flattenParents = (units: OrgUnit[]): OrgUnit[] =>
  units.flatMap((u) => (u.level <= 3 ? [u, ...flattenParents(u.children ?? [])] : []));

const findUnit = (units: OrgUnit[], id: string): OrgUnit | undefined => {
  for (const u of units) {
    if (u.id === id) return u;
    const hit = findUnit(u.children ?? [], id);
    if (hit) return hit;
  }
  return undefined;
};

/* ---- parent picker (Dropdown of eligible units) ---- */

interface ParentPickerProps {
  options: OrgUnit[];
  value: OrgUnit | undefined;
  error?: boolean;
  onChange: (id: string) => void;
}

const ParentPicker = ({ options, value, error = false, onChange }: ParentPickerProps) => {
  const { t } = useTranslation();
  const levelConfig = useLevelConfig();
  const [open, setOpen] = useState(false);

  return (
    <Dropdown
      open={open}
      onClose={() => setOpen(false)}
      align='start'
      className='unit-drawer__picker'
      trigger={
        <button
          type='button'
          className={`unit-drawer__picker-trigger${error ? ' unit-drawer__picker-trigger--error' : ''}`}
          aria-expanded={open}
          aria-invalid={error || undefined}
          onClick={() => setOpen((o) => !o)}
        >
          {value ? (
            <span className='unit-drawer__picker-value'>
              <Icon
                name={levelConfig(value.level).icon}
                size={17}
                className='unit-drawer__picker-icon'
              />
              <span className='unit-drawer__picker-name'>{value.name}</span>
            </span>
          ) : (
            <span className='unit-drawer__picker-placeholder'>{t('unit_field_parent')}</span>
          )}
          <Icon name='chevron-down' size={16} className='unit-drawer__picker-caret' />
        </button>
      }
    >
      <div className='unit-drawer__picker-list'>
        {options.map((u) => {
          const isSelected = u.id === value?.id;
          return (
            <button
              key={u.id}
              type='button'
              role='menuitem'
              className={`app-dropdown__item unit-drawer__option${isSelected ? ' is-selected' : ''}`}
              onClick={() => {
                onChange(u.id);
                setOpen(false);
              }}
            >
              <Icon
                name={levelConfig(u.level).icon}
                size={16}
                className='unit-drawer__option-icon'
              />
              <span className='unit-drawer__picker-label'>
                <span className='unit-drawer__picker-name'>{u.name}</span>
                <span className='unit-drawer__picker-sub'>{levelConfig(u.level).name}</span>
              </span>
              <Icon
                name={isSelected ? 'check' : 'plus'}
                size={16}
                className='unit-drawer__option-marker'
              />
            </button>
          );
        })}
      </div>
    </Dropdown>
  );
};

/* ---- the drawer ---- */

const CreateUnitDrawer = ({ open, parentId, onClose, onCreated }: CreateUnitDrawerProps) => {
  const { t } = useTranslation();
  const levelConfig = useLevelConfig();
  const { data: roots = [] } = useGetCompanyStructureQuery();
  const [addNode, { isLoading: creating }] = useAddNodeMutation();
  const [draft, setDraft] = useState(emptyDraft);
  // Errors show only after the first Create attempt, then clear live.
  const [submitted, setSubmitted] = useState(false);

  const parentOptions = useMemo(() => flattenParents(roots), [roots]);

  // Re-seed the draft each time the drawer opens; default the parent to the
  // requested unit when eligible, else the company root.
  useEffect(() => {
    if (open) {
      const wanted = parentId ? findUnit(roots, parentId) : undefined;
      const fallback = parentOptions[0];
      setDraft({
        ...emptyDraft,
        parentId: (wanted && wanted.level <= 3 ? wanted : fallback)?.id ?? '',
      });
      setSubmitted(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, parentId]);

  const parent = draft.parentId ? findUnit(roots, draft.parentId) : undefined;
  const childLevel = parent ? parent.level + 1 : 1;
  // Parent is validated on the RESOLVED unit — a stale id that no longer maps
  // to a node counts as missing.
  const errors = validateUnitDraft(
    { ...draft, parentId: parent?.id ?? '' },
    { parentRequired: true },
  );
  const shown = submitted ? errors : {};

  const patch = (changes: Partial<UnitDraft>) => setDraft((d) => ({ ...d, ...changes }));

  const create = async () => {
    setSubmitted(true);
    if (hasErrors(errors) || !parent) return;
    try {
      await addNode({
        name: draft.name.trim(),
        description: draft.description.trim(),
        parent: parent.id,
        // Optional location fields → null when empty.
        location: draft.location.trim() || null,
        googleMapLink: draft.googleMapLink.trim() || null,
        coordinates: draft.coordinates.trim() || null,
        radius: draft.radius,
        tags: draft.tagIds,
      }).unwrap();
      toast.success(t('unit_created'));
      onCreated(parent.id);
      onClose();
    } catch {
      toast.error(t('error_generic'));
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      className='unit-drawer--form'
      icon='plus'
      title={t('unit_new_title')}
      subtitle={t('unit_new_subtitle')}
    >
      <Card padded={false} className='unit-drawer__card'>
        <div className='unit-drawer__card-head'>
          <h3 className='unit-drawer__card-title'>{t('unit_new_card_title')}</h3>
          <p className='unit-drawer__card-subtitle'>{t('unit_new_card_subtitle')}</p>
        </div>

        <UnitFields
          draft={draft}
          patch={patch}
          errors={shown}
          namePlaceholder={t('unit_name_placeholder', { level: levelConfig(childLevel).name })}
          parentField={
            <Field
              label={t('unit_field_parent')}
              required
              error={shown.parent ? t('unit_parent_required') : undefined}
            >
              <ParentPicker
                options={parentOptions}
                value={parent}
                error={!!shown.parent}
                onChange={(id) => patch({ parentId: id })}
              />
            </Field>
          }
          tierField={
            <Field label={t('unit_field_level')}>
              <div className='unit-drawer__level'>
                <Chip variant='neutral'>{t('structure_level', { n: childLevel })}</Chip>
                <span className='unit-drawer__level-name'>“{levelConfig(childLevel).name}”</span>
              </div>
            </Field>
          }
        />
      </Card>

      <div className='unit-drawer__actions'>
        <Button variant='secondary' onClick={onClose} disabled={creating}>
          {t('button_cancel')}
        </Button>
        {/* Stays enabled so a click can surface the errors. */}
        <Button iconStart='plus' onClick={create} loading={creating}>
          {t('unit_create')}
        </Button>
      </div>
    </Drawer>
  );
};

export default CreateUnitDrawer;
